import React, { memo } from "react";
import { validators } from "investira.sdk";

import {
  Stack,
  Typography,
  Card,
  Tooltip,
  Icon,
} from "investira.react.components";
import { formats } from "investira.sdk";

// β = 1: O ativo tende a seguir o mercado perfeitamente. Se o mercado sobe 1%, o ativo tende a subir 1%.
// β > 1 (ex.: 1.5): O ativo é mais volátil que o mercado. É considerado mais agressivo/arriscado. Se o mercado sobe 1%, o ativo tende a subir 1,5%. Se o mercado cai 1%, ele tende a cair 1,5%.
// β < 1 (ex.: 0.7): O ativo é menos volátil que o mercado. É considerado mais defensivo/conservador. Se o mercado sobe 1%, o ativo tende a subir apenas 0,7%.
// β = 0: O retorno do ativo não tem correlação com os movimentos do mercado (ex.: um título público de um país muito estável).
// β < 0: O ativo se move na direção oposta ao mercado (um comportamento raro, associado a hedges como o ouro em certos cenários).

const BetaCard = memo((props) => {
  const { value = 0, label = "Sharpe", tooltip } = props;

  const palette = (pValue) => {
    // Função que retorna a cor de acordo com o valor do Sharpe
    if (pValue === 0) {
      return {
        color: "text.primary",
      };
    } else if (pValue < 0) {
      return {
        backgroundColor: "error.main",
        color: "error.contrastText", // Cor de contraste para texto em fundo escuro
      };
    } else if (pValue > 0 && pValue < 1) {
      return {
        backgroundColor: "warning.main",
        color: "background.dark",
      };
    } else if (pValue === 1) {
      return {
        backgroundColor: "info.highlight",
        color: "info.contrastText",
      };
    } else if (pValue > 1) {
      return {
        backgroundColor: "primary.main",
        color: "primary.contrastText",
      };
    }
    // Valor padrão caso não se encaixe em nenhum critério
    return {
      color: "text.primary",
    };
  };

  // Função que retorna uma frase curta e humanizada de acordo com o valor do Sharpe
  const sharpeDescricao = (pValue) => {
    if (validators.isNull(pValue)) return "Sem informação";
    if (pValue < 0) return "Sem correlação oposta ao benchmark";
    if (pValue === 0) return "Sem correlação com o benchmark";
    if (pValue > 0 && pValue < 1) return "Menos volátil que o benchmark";
    if (pValue === 1) return "Segue o benchmark";
    if (pValue > 1) return "Mais volátil que o benchmark";
    return "Sem classificação";
  };

  return (
    <Card
      sx={{
        flex: 1,
        display: "flex",
        minHeight: 0,
        ...palette(value),
      }}
    >
      <Stack px={2} py={2} flexGrow={1} justifyContent="space-between">
        <Stack direction="row" justifyContent="space-between">
          <Typography
            variant="caption"
            color="inherit"
            sx={{ fontWeight: 500 }}
          >
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip}>
              <span>
                <Icon size={16} color={"inherit"} iconName="information" />
              </span>
            </Tooltip>
          )}
        </Stack>
        <Stack direction="column" justifyContent="flex-end">
          <Stack>
            <Typography variant={{ xs: "h6", xl: "h5" }} color="inherit">
              <b>
                {!validators.isNull(value)
                  ? formats.formatNumber(value, 3, true)
                  : `---`}
              </b>
            </Typography>
          </Stack>
          <Stack>
            <Typography
              variant={"caption"}
              color="inherit"
              sx={{ fontWeight: 500 }}
            >
              {sharpeDescricao(value)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
});

export default BetaCard;
